import { useV2GetCheckoutQuery } from "@stepapp/core";
import {
  Block,
  Button,
  palette,
  SafeAreaView,
  SvgIcon,
  spacing,
  Touchable,
  Typography,
} from "@stepapp/ui";
import { useRouter } from "expo-router";
import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { AddCard } from "../../../src/components/Payment/AddCard";
import { PaymentMethods } from "../../../src/components/Payment/PaymentMethods";
import type { ReservationFormParams } from "../../../src/store/draftReservation/types";

const ServicePaymentMethodModal = () => {
  const { t } = useTranslation();
  const { back } = useRouter();
  const [loading, setLoading] = useState(false);
  const [addNewCard, setAddNewCard] = useState(false);
  const { setValue, watch } = useFormContext<ReservationFormParams>();

  const { paymentMethodId } = watch();

  const { data } = useV2GetCheckoutQuery({
    fetchPolicy: "cache-and-network",
  });

  const checkout = data?.v2GetCheckout;

  const selectPaymentMethod = useCallback(
    (id: string) => {
      setValue("paymentMethodId", id);
      back();
    },
    [setValue, back],
  );

  const handleClickBackIcon = useCallback(() => {
    if (!addNewCard) {
      back();
    }
    setAddNewCard(false);
  }, [back, addNewCard]);

  return (
    <SafeAreaView edges={["bottom"]} style={styles.container}>
      <Block row alignCenter>
        <Touchable onPress={handleClickBackIcon}>
          <Block alignCenter justifyCenter style={styles.backIcon}>
            <SvgIcon color={palette.common.black} name="chevron-left" size={24} />
          </Block>
        </Touchable>
        <Block alignCenter>
          <Typography variant="sectionTitle">
            {addNewCard ? t("pages.chooseCard.newCard") : t("pages.chooseCard.chooseCard")}
          </Typography>
        </Block>
      </Block>

      {addNewCard ? (
        <Block marginTop={4}>
          <AddCard
            showPaymentNotice
            loading={loading}
            setLoading={setLoading}
            onComplete={selectPaymentMethod}
          />
        </Block>
      ) : (
        <>
          <Block marginTop={1} marginBottom={4}>
            <Typography type="secondary" variant="lightLabel">
              {t("screens.serviceSummary.paymentNotice")}
            </Typography>
          </Block>

          <Block>
            <PaymentMethods
              paymentMethodId={paymentMethodId || ""}
              selectPaymentMethod={selectPaymentMethod}
              cycleDuration={checkout?.periodicity?.cycleDuration || ""}
              price={checkout?.priceAfterDiscounts || 0}
              bundleName={checkout?.bundle?.name || ""}
              startDate={checkout?.startDate || ""}
              setLoading={setLoading}
            />

            <Block row marginBottom={3} marginTop={0.5}>
              <Button
                containerStyle={styles.addNewCardBtn}
                disabled={loading}
                label={t("pages.chooseCard.newCard")}
                onPress={() => setAddNewCard(true)}
                type="secondary"
                variant="small"
                testID="addNewCard"
              />
            </Block>
          </Block>

          <Block flex={1} justifyEnd>
            <Button label={t("pages.chooseCard.confirm")} onPress={() => back()} type="primary" />
          </Block>
        </>
      )}
    </SafeAreaView>
  );
};

export default ServicePaymentMethodModal;

const styles = StyleSheet.create({
  addNewCardBtn: {
    paddingHorizontal: spacing.unit * 4,
  },
  backIcon: {
    backgroundColor: palette.secondary.grey,
    borderRadius: spacing.unit * 2.5,
    height: spacing.unit * 5,
    width: spacing.unit * 5,
    marginRight: spacing.unit * 1.5,
  },
  container: {
    flex: 1,
    paddingTop: spacing.unit * 2,
    paddingBottom: spacing.unit * 3,
  },
});
